import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Check, Home, Mail, FileText } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Dialog, DialogContent } from '@/components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_c9a69f8c-f09a-4a22-b109-5dc5ad9f5a15/artifacts/wa2rgwwr_App%20Development.png";

const SERVICES = [
  { id: 'loan', icon: '⚡', name: 'Payday Loan', desc: 'Same-day cash advance' },
  { id: 'vehicle', icon: '🚗', name: 'Vehicle Finance', desc: 'New & used cars' },
  { id: 'insurance', icon: '🛡️', name: 'Insurance', desc: 'Life, car & home cover' },
  { id: 'bank', icon: '💎', name: 'Diamond Card', desc: 'Premium banking card' },
  { id: 'home', icon: '🏠', name: 'Home Loan', desc: 'Best bond rates in SA', full: true },
];

const SERVICE_LABELS = {
  loan: 'Payday Loan', vehicle: 'Vehicle Finance',
  insurance: 'Insurance', bank: 'Diamond Card', home: 'Home Loan',
};

const INSURANCE_TYPES = [
  { id: 'funeral', name: 'Funeral Insurance', icon: '🕊️' },
  { id: 'life', name: 'Life Insurance', icon: '❤️' },
  { id: 'medical', name: 'Medical Insurance', icon: '🏥' },
  { id: 'vehicle_ins', name: 'Vehicle Insurance', icon: '🚗' },
  { id: 'pet', name: 'Pet Insurance', icon: '🐾' },
  { id: 'house', name: 'House Insurance', icon: '🏠' },
];

export default function ApplyPage() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState([]);
  const [step, setStep] = useState('landing');
  const [queue, setQueue] = useState([]);
  const [queueIdx, setQueueIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [refNo, setRefNo] = useState('');
  const [consent, setConsent] = useState(false);
  const [finalConsent, setFinalConsent] = useState(false);
  const formRef = useRef(null);

  // Email application
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailFormData, setEmailFormData] = useState({});
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [showEmailSuccess, setShowEmailSuccess] = useState(false);

  // Form data state
  const [personal, setPersonal] = useState({});
  const [loanData, setLoanData] = useState({});
  const [vehicleData, setVehicleData] = useState({});
  const [insuranceData, setInsuranceData] = useState({});
  const [bankData, setBankData] = useState({});
  const [homeData, setHomeData] = useState({});

  const toggleService = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };

  const startApp = () => {
    if (selected.length === 0) { toast.error('Please select at least one service'); return; }
    const q = ['personal', ...selected, 'review'];
    setQueue(q);
    setQueueIdx(0);
    setStep('personal');
  };

  const next = () => {
    if (formRef.current) formRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    const nextIdx = queueIdx + 1;
    if (nextIdx < queue.length) {
      setQueueIdx(nextIdx);
      setStep(queue[nextIdx]);
    }
  };

  const prev = () => {
    if (formRef.current) formRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    if (queueIdx <= 0) { setStep('landing'); return; }
    const prevIdx = queueIdx - 1;
    setQueueIdx(prevIdx);
    setStep(queue[prevIdx]);
  };

  const goHome = () => navigate('/');

  const progress = queue.length > 0 ? ((queueIdx + 1) / queue.length) * 100 : 0;

  const submitEmailApp = async () => {
    if (!emailFormData.name || !emailFormData.surname || !emailFormData.cell) {
      toast.error('Please fill in name, surname and cell number');
      return;
    }
    setEmailSubmitting(true);
    try {
      await axios.post(`${API}/api/applications/email-request`, {
        services: selected,
        name: emailFormData.name,
        surname: emailFormData.surname,
        cell_number: emailFormData.cell,
        email: emailFormData.email || '',
      });
      setShowEmailForm(false);
      setShowEmailSuccess(true);
    } catch {
      toast.error('Submission failed. Please try again.');
    } finally {
      setEmailSubmitting(false);
    }
  };

  const submitApp = async () => {
    if (!finalConsent) { toast.error('Please accept the terms'); return; }
    setSubmitting(true);
    try {
      const payload = {
        services: selected,
        personal: {
          full_name: personal.name || '',
          id_number: personal.id || '',
          dob: personal.dob || '',
          gender: personal.gender || '',
          marital_status: personal.marital || '',
          nationality: personal.nationality || '',
          cell_number: personal.cell || '',
          email: personal.email || '',
          address: personal.addr || '',
          city: personal.city || '',
          postal_code: personal.postal || '',
          employment_status: personal.employ || '',
          employer: personal.employer || '',
          gross_income: personal.income || '',
          net_income: personal.net || '',
        },
        ...(selected.includes('loan') && { loan: loanData }),
        ...(selected.includes('vehicle') && { vehicle: vehicleData }),
        ...(selected.includes('insurance') && { insurance: insuranceData }),
        ...(selected.includes('bank') && { bank: bankData }),
        ...(selected.includes('home') && { home: homeData }),
      };
      const res = await axios.post(`${API}/api/applications/submit`, payload);
      setRefNo(res.data.reference);
      setStep('success');
    } catch {
      toast.error('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const restart = () => {
    setSelected([]); setStep('landing'); setQueue([]);
    setQueueIdx(0); setPersonal({}); setLoanData({});
    setVehicleData({}); setInsuranceData({});
    setBankData({}); setHomeData({});
    setConsent(false); setFinalConsent(false);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="apply-page">
      <div ref={formRef} className="max-w-lg mx-auto px-4 py-6 min-h-screen overflow-y-auto">
        {step === 'landing' && (
          <LandingPage
            selected={selected}
            toggleService={toggleService}
            startApp={startApp}
            onEmailApp={() => {
              if (selected.length === 0) { toast.error('Please select at least one service'); return; }
              setEmailFormData({});
              setShowEmailForm(true);
            }}
            navigate={navigate}
          />
        )}
        {step === 'personal' && (
          <FormPage title="Personal Registration" stepLabel={`Step ${queueIdx + 1} of ${queue.length}`} progress={progress} onBack={prev} onNext={next} onHome={goHome} consent={consent} setConsent={setConsent} consentText="I consent to AfriPerx performing a credit check and verifying my identity via the National Credit Bureau.">
            <PersonalForm data={personal} setData={setPersonal} />
          </FormPage>
        )}
        {step === 'loan' && (
          <FormPage title="Payday Loan" stepLabel={`Step ${queueIdx + 1} of ${queue.length}`} progress={progress} onBack={prev} onNext={next} onHome={goHome}>
            <LoanForm data={loanData} setData={setLoanData} />
          </FormPage>
        )}
        {step === 'vehicle' && (
          <FormPage title="Vehicle Finance" stepLabel={`Step ${queueIdx + 1} of ${queue.length}`} progress={progress} onBack={prev} onNext={next} onHome={goHome}>
            <VehicleForm data={vehicleData} setData={setVehicleData} />
          </FormPage>
        )}
        {step === 'insurance' && (
          <FormPage title="Insurance Application" stepLabel={`Step ${queueIdx + 1} of ${queue.length}`} progress={progress} onBack={prev} onNext={next} onHome={goHome}>
            <InsuranceForm data={insuranceData} setData={setInsuranceData} />
          </FormPage>
        )}
        {step === 'bank' && (
          <FormPage title="Diamond Card" stepLabel={`Step ${queueIdx + 1} of ${queue.length}`} progress={progress} onBack={prev} onNext={next} onHome={goHome}>
            <BankForm data={bankData} setData={setBankData} />
          </FormPage>
        )}
        {step === 'home' && (
          <FormPage title="Home Loan" stepLabel={`Step ${queueIdx + 1} of ${queue.length}`} progress={progress} onBack={prev} onNext={next} onHome={goHome}>
            <HomeForm data={homeData} setData={setHomeData} />
          </FormPage>
        )}
        {step === 'review' && (
          <ReviewPage selected={selected} personal={personal} finalConsent={finalConsent} setFinalConsent={setFinalConsent} onBack={prev} onSubmit={submitApp} submitting={submitting} onHome={goHome} />
        )}
        {step === 'success' && <SuccessPage refNo={refNo} onRestart={restart} onHome={goHome} />}
      </div>

      {/* Email Application Dialog */}
      <Dialog open={showEmailForm} onOpenChange={setShowEmailForm}>
        <DialogContent className="bg-white border-gray-200 max-w-sm">
          <div className="text-center mb-4">
            <Mail className="w-10 h-10 text-afriperx-gold mx-auto mb-2" />
            <h3 className="font-syne font-bold text-gray-900 text-lg">Email Application</h3>
            <p className="text-gray-500 text-xs mt-1">Enter your details and a call centre agent will contact you shortly.</p>
          </div>
          <div className="space-y-3">
            <FInput label="Name" value={emailFormData.name} onChange={(e) => setEmailFormData({ ...emailFormData, name: e.target.value })} placeholder="Your first name" data-testid="email-name" />
            <FInput label="Surname" value={emailFormData.surname} onChange={(e) => setEmailFormData({ ...emailFormData, surname: e.target.value })} placeholder="Your surname" data-testid="email-surname" />
            <FInput label="Mobile Number" value={emailFormData.cell} onChange={(e) => setEmailFormData({ ...emailFormData, cell: e.target.value })} placeholder="07X XXX XXXX" maxLength={10} data-testid="email-cell" />
            <FInput label="Email Address (optional)" value={emailFormData.email} onChange={(e) => setEmailFormData({ ...emailFormData, email: e.target.value })} placeholder="you@email.com" data-testid="email-email" />
          </div>
          <button onClick={submitEmailApp} disabled={emailSubmitting} className="w-full mt-4 bg-afriperx-green hover:bg-afriperx-green-dark disabled:opacity-50 text-white font-syne font-bold py-3 rounded-xl transition-colors text-sm" data-testid="submit-email-app-btn">
            {emailSubmitting ? 'Submitting...' : 'Submit Application'}
          </button>
        </DialogContent>
      </Dialog>

      {/* Email Success Dialog */}
      <Dialog open={showEmailSuccess} onOpenChange={setShowEmailSuccess}>
        <DialogContent className="bg-white border-gray-200 max-w-sm text-center">
          <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <Check className="w-7 h-7 text-green-600" />
          </div>
          <h3 className="font-syne font-bold text-gray-900 text-lg">Request Received!</h3>
          <p className="text-gray-500 text-sm mt-2 mb-4 leading-relaxed">
            We received your request. Your information has been submitted and a <strong>Call Centre agent will contact you shortly</strong>.
          </p>
          <button onClick={() => setShowEmailSuccess(false)} className="w-full bg-afriperx-green hover:bg-afriperx-green-dark text-white font-syne font-bold py-3 rounded-xl transition-colors text-sm">
            Close
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ========== LOGO ========== */
const Logo = () => (
  <div className="flex items-center gap-2 mb-6">
    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-afriperx-gold to-afriperx-green flex items-center justify-center">
      <span className="text-white font-bold text-base">A</span>
    </div>
    <span className="font-syne font-bold text-lg text-gray-900">Afri<span className="text-afriperx-gold">Perx</span></span>
  </div>
);

/* ========== LANDING ========== */
const LandingPage = ({ selected, toggleService, startApp, onEmailApp, navigate }) => (
  <div className="animate-in fade-in duration-300" data-testid="landing-section">
    <Logo />
    <div className="inline-block text-[10px] uppercase tracking-widest text-afriperx-green font-semibold bg-green-50 px-3 py-1 rounded-full mb-3">Digital Financial Services & Rewards</div>
    <h1 className="font-syne text-3xl font-bold text-gray-900 leading-tight mb-2">Financial Solutions<br /><em className="text-afriperx-gold not-italic">Built for You</em></h1>
    <p className="text-gray-500 text-sm mb-6">Select the services you need — each gets its own dedicated application. Apply in under 60 seconds.</p>
    <div className="grid grid-cols-2 gap-3 mb-6" data-testid="service-grid">
      {SERVICES.map(s => (
        <div
          key={s.id}
          onClick={() => toggleService(s.id)}
          className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all ${s.full ? 'col-span-2' : ''}
            ${selected.includes(s.id)
              ? 'border-afriperx-gold bg-amber-50 shadow-sm'
              : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          data-testid={`service-${s.id}`}
        >
          {selected.includes(s.id) && (
            <div className="absolute top-2 right-2 w-5 h-5 bg-afriperx-green rounded-full flex items-center justify-center">
              <Check className="w-3 h-3 text-white" />
            </div>
          )}
          <span className="text-2xl">{s.icon}</span>
          <p className="font-syne font-bold text-gray-900 text-sm mt-1">{s.name}</p>
          <p className="text-gray-400 text-xs">{s.desc}</p>
        </div>
      ))}
    </div>

    {/* Two action buttons */}
    <div className="grid grid-cols-2 gap-3 mb-3">
      <button onClick={startApp} className="bg-afriperx-green hover:bg-afriperx-green-dark text-white font-syne font-bold py-3.5 rounded-xl transition-colors text-sm flex items-center justify-center gap-2" data-testid="apply-now-btn">
        <FileText className="w-4 h-4" /> Apply Now
      </button>
      <button onClick={onEmailApp} className="bg-afriperx-gold hover:bg-afriperx-gold-dark text-white font-syne font-bold py-3.5 rounded-xl transition-colors text-sm flex items-center justify-center gap-2" data-testid="email-application-btn">
        <Mail className="w-4 h-4" /> Email Application
      </button>
    </div>

    <p className="text-center text-gray-400 text-xs mt-4">Secure • Confidential • Powered by Trusted Partners</p>
    <button onClick={() => navigate('/login')} className="w-full mt-3 text-gray-500 text-xs hover:text-afriperx-green transition-colors">Already have an account? Sign in</button>
    <button onClick={() => navigate('/')} className="w-full mt-1 text-gray-400 text-xs hover:text-gray-600 transition-colors flex items-center justify-center gap-1"><Home className="w-3 h-3" /> Back to Home</button>
  </div>
);

/* ========== FORM PAGE WRAPPER ========== */
const FormPage = ({ title, stepLabel, progress, onBack, onNext, onHome, children, consent, setConsent, consentText }) => (
  <div className="animate-in slide-in-from-right duration-200" data-testid={`form-${title.toLowerCase().replace(/ /g, '-')}`}>
    <Logo />
    <div className="mb-4">
      <div className="flex justify-between text-xs text-gray-400 mb-1"><span>{title}</span><span>{stepLabel}</span></div>
      <div className="h-1.5 bg-gray-100 rounded-full"><div className="h-1.5 bg-afriperx-green rounded-full transition-all duration-500" style={{ width: `${progress}%` }} /></div>
    </div>
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        <button onClick={onBack} className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50" data-testid="back-btn">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <h2 className="font-syne font-bold text-gray-900">{title}</h2>
      </div>
      <button onClick={onHome} className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50" data-testid="home-btn">
        <Home className="w-4 h-4" />
      </button>
    </div>
    {children}
    {consentText && (
      <label className="flex items-start gap-3 mt-4 cursor-pointer" data-testid="consent-check">
        <div onClick={() => setConsent(!consent)} className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${consent ? 'bg-afriperx-green border-afriperx-gold' : 'border-gray-300'}`}>
          {consent && <Check className="w-3 h-3 text-white" />}
        </div>
        <span className="text-gray-500 text-xs leading-relaxed">{consentText}</span>
      </label>
    )}
    <div className="flex gap-3 mt-4">
      <button onClick={onBack} className="flex-1 border border-gray-200 text-gray-600 font-syne font-bold py-3 rounded-xl transition-colors text-sm hover:bg-gray-50 flex items-center justify-center gap-1" data-testid="back-bottom-btn">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>
      <button onClick={onNext} className="flex-[2] bg-afriperx-green hover:bg-afriperx-green-dark text-white font-syne font-bold py-3 rounded-xl transition-colors text-sm" data-testid="continue-btn">
        Continue <span className="ml-1">→</span>
      </button>
    </div>
    <div className="h-6" />
  </div>
);

/* ========== FORM COMPONENTS ========== */
const FInput = ({ label, value, onChange, ...props }) => (
  <div className="mb-3">
    <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
    <input value={value || ''} onChange={onChange} className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-900 focus:border-afriperx-gold focus:ring-1 focus:ring-afriperx-gold outline-none transition-colors" {...props} />
  </div>
);

const FSelect = ({ label, value, onChange, options, placeholder }) => (
  <div className="mb-3">
    <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
    <select value={value || ''} onChange={onChange} className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-900 focus:border-afriperx-gold focus:ring-1 focus:ring-afriperx-gold outline-none transition-colors">
      <option value="">{placeholder || 'Select'}</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

const FRadio = ({ label, options, value, onChange }) => (
  <div className="mb-3">
    {label && <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>}
    <div className="flex flex-wrap gap-2">
      {options.map(o => (
        <button key={o} type="button" onClick={() => onChange(o)} className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${value === o ? 'bg-afriperx-green text-white border-afriperx-gold' : 'border-gray-200 text-gray-600 bg-white hover:border-gray-300'}`}>
          {o}
        </button>
      ))}
    </div>
  </div>
);

const FCheckToggle = ({ items, selected, onChange }) => (
  <div className="grid grid-cols-2 gap-2 mb-4">
    {items.map(item => {
      const isOn = selected.includes(item.id);
      return (
        <div
          key={item.id}
          onClick={() => {
            const next = isOn ? selected.filter(i => i !== item.id) : [...selected, item.id];
            onChange(next);
          }}
          className={`cursor-pointer rounded-xl border-2 p-3 transition-all flex items-center gap-2 ${isOn ? 'border-afriperx-gold bg-amber-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}
          data-testid={`ins-type-${item.id}`}
        >
          {isOn && <div className="w-4 h-4 bg-afriperx-green rounded-full flex items-center justify-center flex-shrink-0"><Check className="w-2.5 h-2.5 text-white" /></div>}
          <span className="text-sm">{item.icon}</span>
          <span className={`text-xs font-medium ${isOn ? 'text-afriperx-gold' : 'text-gray-600'}`}>{item.name}</span>
        </div>
      );
    })}
  </div>
);

const SectionTitle = ({ children }) => <div className="text-xs font-bold text-gray-900 uppercase tracking-wider mt-5 mb-3 pb-1 border-b border-gray-100">{children}</div>;

/* ========== PERSONAL FORM ========== */
const PersonalForm = ({ data, setData }) => {
  const u = (k) => (e) => setData({ ...data, [k]: e.target.value });
  const ur = (k) => (v) => setData({ ...data, [k]: v });
  return (
    <>
      <SectionTitle>Personal Details</SectionTitle>
      <FInput label="Full Name (as per ID)" value={data.name} onChange={u('name')} placeholder="e.g. Thabo Nkosi" data-testid="p-name" />
      <div className="grid grid-cols-2 gap-3">
        <FInput label="SA ID Number" value={data.id} onChange={u('id')} placeholder="13 digits" maxLength={13} data-testid="p-id" />
        <FInput label="Date of Birth" value={data.dob} onChange={u('dob')} type="date" data-testid="p-dob" />
      </div>
      <FRadio label="Gender" options={['Male', 'Female']} value={data.gender} onChange={ur('gender')} />
      <FSelect label="Marital Status" value={data.marital} onChange={u('marital')} options={['Single', 'Married (COP)', 'Married (ANC)', 'Divorced', 'Widowed']} />
      <FSelect label="Nationality" value={data.nationality} onChange={u('nationality')} options={['South African', 'Zimbabwean', 'Mozambican', 'Lesotho']} />
      <SectionTitle>Contact Information</SectionTitle>
      <FInput label="Cell Number" value={data.cell} onChange={u('cell')} placeholder="07X XXX XXXX" maxLength={10} data-testid="p-cell" />
      <FInput label="Email Address" value={data.email} onChange={u('email')} type="email" placeholder="you@email.com" data-testid="p-email" />
      <FInput label="Residential Address" value={data.addr} onChange={u('addr')} placeholder="Street address" />
      <div className="grid grid-cols-2 gap-3">
        <FInput label="City" value={data.city} onChange={u('city')} placeholder="City" />
        <FInput label="Postal Code" value={data.postal} onChange={u('postal')} placeholder="0000" maxLength={4} />
      </div>
      <SectionTitle>Employment & Income</SectionTitle>
      <FRadio label="Employment Status" options={['Employed', 'Self-Employed', 'Unemployed']} value={data.employ} onChange={ur('employ')} />
      <FInput label="Employer Name" value={data.employer} onChange={u('employer')} placeholder="Company name" />
      <div className="grid grid-cols-2 gap-3">
        <FInput label="Gross Monthly Income" value={data.income} onChange={u('income')} placeholder="R 00,000" />
        <FInput label="Net Monthly Income" value={data.net} onChange={u('net')} placeholder="R 00,000" />
      </div>
    </>
  );
};

/* ========== LOAN FORM ========== */
const LoanForm = ({ data, setData }) => {
  const u = (k) => (e) => setData({ ...data, [k]: e.target.value });
  const ur = (k) => (v) => setData({ ...data, [k]: v });
  return (
    <>
      <SectionTitle>Loan Details</SectionTitle>
      <FInput label="Loan Amount Required" value={data.amount} onChange={u('amount')} placeholder="R 500 – R 8,000" />
      <FRadio label="Repayment Term" options={['1 Month', '2 Months', '3 Months', '6 Months']} value={data.term} onChange={ur('term')} />
      <FSelect label="Reason for Loan" value={data.reason} onChange={u('reason')} options={['Emergency expenses', 'Medical bills', 'Rental arrears', 'Groceries & utilities', 'Education fees']} />
      <FInput label="Payday Date (next salary)" value={data.payday} onChange={u('payday')} type="date" />
      <SectionTitle>Banking Details</SectionTitle>
      <FSelect label="Bank Name" value={data.bank} onChange={u('bank')} options={['Absa', 'Standard Bank', 'FNB', 'Nedbank', 'Capitec', 'African Bank']} />
      <div className="grid grid-cols-2 gap-3">
        <FInput label="Account Number" value={data.accno} onChange={u('accno')} placeholder="Account no." />
        <FSelect label="Account Type" value={data.acctype} onChange={u('acctype')} options={['Cheque', 'Savings', 'Transmission']} />
      </div>
      <FInput label="Branch Code" value={data.branch} onChange={u('branch')} placeholder="e.g. 632005" />
      <SectionTitle>Monthly Expenses</SectionTitle>
      <div className="grid grid-cols-2 gap-3">
        <FInput label="Rent / Bond" value={data.rent} onChange={u('rent')} placeholder="R 0,000" />
        <FInput label="Groceries" value={data.groceries} onChange={u('groceries')} placeholder="R 0,000" />
        <FInput label="Transport" value={data.transport} onChange={u('transport')} placeholder="R 0,000" />
        <FInput label="Other Loans" value={data.otherloans} onChange={u('otherloans')} placeholder="R 0,000" />
      </div>
    </>
  );
};

/* ========== VEHICLE FORM ========== */
const VehicleForm = ({ data, setData }) => {
  const u = (k) => (e) => setData({ ...data, [k]: e.target.value });
  const ur = (k) => (v) => setData({ ...data, [k]: v });
  return (
    <>
      <SectionTitle>Vehicle Details</SectionTitle>
      <FRadio label="Vehicle Condition" options={['New', 'Used', 'Demo']} value={data.condition} onChange={ur('condition')} />
      <div className="grid grid-cols-2 gap-3">
        <FInput label="Make" value={data.make} onChange={u('make')} placeholder="e.g. Toyota" />
        <FInput label="Model" value={data.model} onChange={u('model')} placeholder="e.g. Hilux" />
        <FInput label="Year" value={data.year} onChange={u('year')} placeholder="2024" maxLength={4} />
        <FInput label="Colour" value={data.colour} onChange={u('colour')} placeholder="e.g. Silver" />
        <FInput label="Mileage (km)" value={data.mileage} onChange={u('mileage')} placeholder="000,000" />
        <FInput label="VIN / Reg No." value={data.vin} onChange={u('vin')} placeholder="Optional" />
      </div>
      <FInput label="Purchase Price" value={data.price} onChange={u('price')} placeholder="R 000,000" />
      <SectionTitle>Finance Details</SectionTitle>
      <FInput label="Deposit Amount" value={data.deposit} onChange={u('deposit')} placeholder="R 00,000 (optional)" />
      <FRadio label="Finance Term" options={['24m', '36m', '48m', '60m', '72m']} value={data.term} onChange={ur('term')} />
      <FRadio label="Balloon Payment" options={['None', '10%', '20%', '30%']} value={data.balloon} onChange={ur('balloon')} />
      <FInput label="Dealer / Seller Name" value={data.dealer} onChange={u('dealer')} placeholder="Dealership or private seller" />
    </>
  );
};

/* ========== INSURANCE FORM (Updated with types) ========== */
const InsuranceForm = ({ data, setData }) => {
  const u = (k) => (e) => setData({ ...data, [k]: e.target.value });
  const ur = (k) => (v) => setData({ ...data, [k]: v });
  const types = data.types || [];
  const setTypes = (v) => setData({ ...data, types: v });

  return (
    <>
      <SectionTitle>Select Insurance Type(s)</SectionTitle>
      <p className="text-gray-400 text-xs mb-3">Choose one or more insurance products:</p>
      <FCheckToggle items={INSURANCE_TYPES} selected={types} onChange={setTypes} />

      {/* Funeral Insurance */}
      {types.includes('funeral') && (
        <>
          <SectionTitle>Funeral Insurance</SectionTitle>
          <FSelect label="Cover Amount" value={data.funeralCover} onChange={u('funeralCover')} options={['R 10,000', 'R 20,000', 'R 30,000', 'R 50,000', 'R 75,000', 'R 100,000']} />
          <FInput label="Number of Dependants to Cover" value={data.funeralDeps} onChange={u('funeralDeps')} placeholder="e.g. 4" />
          <FRadio label="Include Extended Family?" options={['Yes', 'No']} value={data.funeralExtended} onChange={ur('funeralExtended')} />
        </>
      )}

      {/* Life Insurance */}
      {types.includes('life') && (
        <>
          <SectionTitle>Life Insurance</SectionTitle>
          <FSelect label="Cover Amount" value={data.lifeCover} onChange={u('lifeCover')} options={['R 100,000', 'R 250,000', 'R 500,000', 'R 1,000,000', 'R 2,000,000+']} />
          <FRadio label="Smoker Status" options={['Non-Smoker', 'Smoker', 'Ex-Smoker']} value={data.smoker} onChange={ur('smoker')} />
          <FRadio label="Pre-existing Medical Conditions?" options={['None', 'Yes']} value={data.medical} onChange={ur('medical')} />
          {data.medical === 'Yes' && <FInput label="Medical Condition Details" value={data.meddetail} onChange={u('meddetail')} placeholder="e.g. Hypertension, Diabetes" />}
        </>
      )}

      {/* Medical Insurance */}
      {types.includes('medical') && (
        <>
          <SectionTitle>Medical Insurance</SectionTitle>
          <FSelect label="Plan Type" value={data.medPlan} onChange={u('medPlan')} options={['Hospital Plan', 'Savings Plan', 'Comprehensive', 'Day-to-Day']} />
          <FInput label="Number of Members" value={data.medMembers} onChange={u('medMembers')} placeholder="e.g. 3" />
          <FRadio label="Chronic Medication Required?" options={['Yes', 'No']} value={data.medChronic} onChange={ur('medChronic')} />
          {data.medChronic === 'Yes' && <FInput label="List Chronic Conditions" value={data.medChronicDetail} onChange={u('medChronicDetail')} placeholder="e.g. Diabetes, Hypertension" />}
        </>
      )}

      {/* Vehicle Insurance */}
      {types.includes('vehicle_ins') && (
        <>
          <SectionTitle>Vehicle Insurance</SectionTitle>
          <FRadio label="Cover Type" options={['Comprehensive', 'Third Party', 'Third Party Fire & Theft']} value={data.vehCoverType} onChange={ur('vehCoverType')} />
          <div className="grid grid-cols-2 gap-3">
            <FInput label="Vehicle Make" value={data.vehMake} onChange={u('vehMake')} placeholder="e.g. Toyota" />
            <FInput label="Vehicle Model" value={data.vehModel} onChange={u('vehModel')} placeholder="e.g. Corolla" />
            <FInput label="Year" value={data.vehYear} onChange={u('vehYear')} placeholder="2024" maxLength={4} />
            <FInput label="Vehicle Value" value={data.vehValue} onChange={u('vehValue')} placeholder="R 000,000" />
          </div>
          <FRadio label="Currently Insured?" options={['Yes', 'No']} value={data.vehCurrentIns} onChange={ur('vehCurrentIns')} />
        </>
      )}

      {/* Pet Insurance */}
      {types.includes('pet') && (
        <>
          <SectionTitle>Pet Insurance</SectionTitle>
          <FRadio label="Pet Type" options={['Dog', 'Cat']} value={data.petType} onChange={ur('petType')} />
          <FInput label="Pet Breed" value={data.petBreed} onChange={u('petBreed')} placeholder="e.g. Labrador" />
          <FInput label="Pet Age (years)" value={data.petAge} onChange={u('petAge')} placeholder="e.g. 3" />
          <FSelect label="Cover Level" value={data.petCover} onChange={u('petCover')} options={['Basic (accidents)', 'Standard (accidents + illness)', 'Comprehensive (full cover)']} />
        </>
      )}

      {/* House Insurance */}
      {types.includes('house') && (
        <>
          <SectionTitle>House Insurance</SectionTitle>
          <FRadio label="Cover Type" options={['Building', 'Contents', 'Both (Building + Contents)']} value={data.houseCoverType} onChange={ur('houseCoverType')} />
          <FInput label="Property Value" value={data.houseValue} onChange={u('houseValue')} placeholder="R 000,000" />
          <FInput label="Contents Value" value={data.houseContents} onChange={u('houseContents')} placeholder="R 000,000" />
          <FInput label="Property Address" value={data.houseAddr} onChange={u('houseAddr')} placeholder="Full address" />
          <FRadio label="Security Features" options={['Alarm', 'Alarm + Armed Response', 'None']} value={data.houseSec} onChange={ur('houseSec')} />
        </>
      )}

      {/* Beneficiary (always shown) */}
      <SectionTitle>Primary Beneficiary</SectionTitle>
      <FInput label="Beneficiary Full Name" value={data.beneName} onChange={u('beneName')} placeholder="Full name" />
      <div className="grid grid-cols-2 gap-3">
        <FSelect label="Relationship" value={data.beneRel} onChange={u('beneRel')} options={['Spouse', 'Child', 'Parent', 'Sibling']} />
        <FInput label="% Share" value={data.benePct} onChange={u('benePct')} placeholder="100%" />
      </div>
      <FInput label="Beneficiary ID Number" value={data.beneId} onChange={u('beneId')} placeholder="13-digit SA ID" />
    </>
  );
};

/* ========== BANK / DIAMOND CARD FORM (cleaned up) ========== */
const BankForm = ({ data, setData }) => {
  const u = (k) => (e) => setData({ ...data, [k]: e.target.value });
  const ur = (k) => (v) => setData({ ...data, [k]: v });
  return (
    <>
      {/* Card visual */}
      <div className="bg-gradient-to-br from-gray-900 via-red-950 to-gray-900 rounded-2xl p-5 mb-4 relative overflow-hidden">
        <div className="absolute top-[-20px] right-[-20px] w-28 h-28 bg-afriperx-green/20 rounded-full blur-xl" />
        <p className="text-[10px] tracking-[0.18em] text-white/40 mb-4 font-semibold">AFRIPERX</p>
        <p className="tracking-[0.22em] text-white/50 mb-2 font-mono text-sm">•••• •••• •••• ••••</p>
        <div className="flex justify-between items-end">
          <div>
            <p className="text-[8px] uppercase text-white/30 tracking-wider">Card Holder</p>
            <p className="font-syne font-bold text-white text-sm">{data.cardname || 'YOUR NAME'}</p>
          </div>
          <span className="text-2xl">💎</span>
        </div>
      </div>
      <SectionTitle>Card Details</SectionTitle>
      <FInput label="Name on Card" value={data.cardname} onChange={u('cardname')} placeholder="As it should appear on card" />
      <FRadio label="Statement Delivery" options={['Email', 'SMS', 'Both']} value={data.statement} onChange={ur('statement')} />
      <FInput label="Card Delivery Address" value={data.delivery} onChange={u('delivery')} placeholder="Delivery address" />
      <SectionTitle>Existing Bank Account (for Linking)</SectionTitle>
      <div className="grid grid-cols-2 gap-3">
        <FSelect label="Current Bank" value={data.bank} onChange={u('bank')} options={['Absa', 'Standard Bank', 'FNB', 'Nedbank', 'Capitec']} />
        <FInput label="Account No." value={data.accno} onChange={u('accno')} placeholder="Number" />
      </div>
    </>
  );
};

/* ========== HOME LOAN FORM ========== */
const HomeForm = ({ data, setData }) => {
  const u = (k) => (e) => setData({ ...data, [k]: e.target.value });
  const ur = (k) => (v) => setData({ ...data, [k]: v });
  return (
    <>
      <SectionTitle>Property Details</SectionTitle>
      <FRadio label="Application Type" options={['Purchase', 'Refinance', 'Build', 'Switch']} value={data.apptype} onChange={ur('apptype')} />
      <FRadio label="Property Type" options={['House', 'Townhouse', 'Flat/Unit', 'Plot']} value={data.proptype} onChange={ur('proptype')} />
      <FInput label="Property Address / Development" value={data.propaddr} onChange={u('propaddr')} placeholder="Property address" />
      <FInput label="Purchase Price / Property Value" value={data.propval} onChange={u('propval')} placeholder="R 000,000" />
      <FInput label="Deposit Available" value={data.deposit} onChange={u('deposit')} placeholder="R 000,000 (or 0 for 100% bond)" />
      <SectionTitle>Bond Details</SectionTitle>
      <FRadio label="Loan Term" options={['10 yrs', '15 yrs', '20 yrs', '25 yrs', '30 yrs']} value={data.term} onChange={ur('term')} />
      <FRadio label="Interest Rate Preference" options={['Variable', 'Fixed 1yr', 'Fixed 2yr']} value={data.rate} onChange={ur('rate')} />
      <FRadio label="Joint Application?" options={['Solo', 'Joint (Spouse)']} value={data.joint} onChange={ur('joint')} />
    </>
  );
};

/* ========== REVIEW ========== */
const ReviewPage = ({ selected, personal, finalConsent, setFinalConsent, onBack, onSubmit, submitting, onHome }) => (
  <div className="animate-in slide-in-from-right duration-200" data-testid="review-page">
    <Logo />
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        <button onClick={onBack} className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50" data-testid="review-back-btn"><ChevronLeft className="w-4 h-4" /></button>
        <h2 className="font-syne font-bold text-gray-900">Review & Submit</h2>
      </div>
      <button onClick={onHome} className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50"><Home className="w-4 h-4" /></button>
    </div>
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
      <p className="text-[10px] uppercase tracking-wider text-afriperx-gold font-semibold mb-2">Applications Submitted</p>
      <div className="flex flex-wrap gap-2">{selected.map(s => <span key={s} className="text-xs bg-white text-afriperx-gold px-3 py-1 rounded-full border border-amber-200 font-medium">{SERVICE_LABELS[s]}</span>)}</div>
    </div>
    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4">
      <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">Applicant</p>
      <p className="font-syne font-bold text-gray-900">{personal.name || '—'}</p>
      <p className="text-gray-500 text-sm">{personal.cell} {personal.email && `• ${personal.email}`}</p>
    </div>
    <p className="text-xs text-gray-400 leading-relaxed mb-4 bg-gray-50 border border-gray-200 rounded-xl p-3">
      By submitting, you authorise AfriPerx (FSP Licensed) to process your application, perform credit checks via the NCR, verify your identity with the DHA, and share your information with relevant financial service partners. All data is handled in accordance with POPIA.
    </p>
    <label className="flex items-start gap-3 mb-4 cursor-pointer" data-testid="final-consent">
      <div onClick={() => setFinalConsent(!finalConsent)} className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${finalConsent ? 'bg-afriperx-green border-afriperx-gold' : 'border-gray-300'}`}>
        {finalConsent && <Check className="w-3 h-3 text-white" />}
      </div>
      <span className="text-gray-500 text-xs leading-relaxed">I have read, understood, and agree to all terms, disclosures, and the POPIA consent above.</span>
    </label>
    <div className="flex gap-3">
      <button onClick={onBack} className="flex-1 border border-gray-200 text-gray-600 font-syne font-bold py-3.5 rounded-xl transition-colors text-sm hover:bg-gray-50 flex items-center justify-center gap-1">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>
      <button onClick={onSubmit} disabled={submitting} className="flex-[2] bg-afriperx-green hover:bg-afriperx-green-dark disabled:opacity-50 text-white font-syne font-bold py-3.5 rounded-xl transition-colors text-sm" data-testid="submit-all-btn">
        {submitting ? 'Submitting...' : 'Submit All Applications →'}
      </button>
    </div>
    <p className="text-center text-gray-400 text-xs mt-3">Secure • Encrypted • POPIA Compliant</p>
  </div>
);

/* ========== SUCCESS ========== */
const SuccessPage = ({ refNo, onRestart, onHome }) => (
  <div className="flex flex-col items-center justify-center min-h-[70vh] text-center animate-in fade-in duration-500" data-testid="success-page">
    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4"><Check className="w-8 h-8 text-green-600" /></div>
    <h2 className="font-syne text-2xl font-bold text-gray-900 mb-2">Application Received!</h2>
    <p className="text-gray-500 text-sm mb-4 max-w-xs">Your applications have been submitted to AfriPerx. A consultant will contact you within 2–4 business hours.</p>
    <div className="bg-gray-900 text-white font-mono text-lg px-6 py-3 rounded-xl mb-2" data-testid="ref-number">REF: {refNo}</div>
    <p className="text-gray-400 text-xs mb-6">Keep your reference number safe.</p>
    <div className="flex gap-3">
      <button onClick={onRestart} className="bg-afriperx-green hover:bg-afriperx-green-dark text-white font-syne font-bold py-3 px-6 rounded-xl transition-colors text-sm">Submit Another</button>
      <button onClick={onHome} className="border border-gray-200 text-gray-600 font-syne font-bold py-3 px-6 rounded-xl transition-colors text-sm hover:bg-gray-50 flex items-center gap-1"><Home className="w-4 h-4" /> Home</button>
    </div>
  </div>
);
